import { apiClient } from './client'
import type {
  AdsManagerCampaign,
  AdsManagerCampaignInput,
  AdsManagerPublicConfig,
  AdsManagerActiveAdvertiserAccess,
  AdsAdvertiserSummary,
  AdsBrandIdentity,
  AdsManagerSession,
  AdsSettings,
  AdsAthenaCostReport,
  AdminCreditGrantResult,
  AdminEmailCreditGrantResult,
  AdminCampaignPricingResult,
  BillingActivity,
  BillingPayment,
  BillingSummary,
  CampaignAnalytics,
  CampaignAnalyticsRange,
  BillingPreferenceResponse,
  BillingPaymentReconcileResponse,
  CampaignDetail,
  CoreCreative,
  DashboardMetrics,
  ReachEstimate,
  ReachCatalogSourceRow,
  ReachCatalogSyncInput,
  TargetOption,
  TargetScope,
  TargetSelection,
  UnfurlResult,
  CursorPage,
} from './adsTypes'

export * from './adsTypes'
export * from './adsBudgetReach'

export const ADS_MANAGER_STORAGE_KEYS = {
  TOKEN: 'yikYakAdsManagerToken',
  ADVERTISER_ID: 'yikYakAdsManagerAdvertiserId',
  DRAFT: 'yikYakAdsManagerDraftV2',
} as const

/**
 * Browser-only notification emitted after the Ads Manager session or active
 * advertiser changes. Consumers use it to immediately discard account-scoped
 * presentation state instead of waiting for a polling interval.
 */
export const ADS_MANAGER_SESSION_CHANGED_EVENT =
  'yik-yak-ads-manager-session-changed'

let memoryToken: string | undefined

const notifyAdsManagerSessionChanged = (): void => {
  if (
    typeof window === 'undefined' ||
    typeof window.dispatchEvent !== 'function'
  ) {
    return
  }
  window.dispatchEvent(new Event(ADS_MANAGER_SESSION_CHANGED_EVENT))
}

const readStorage = (
  storage: Storage | undefined,
  key: string,
): string | null => {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

const writeStorage = (
  storage: Storage | undefined,
  key: string,
  value?: string,
): void => {
  try {
    if (value === undefined) storage?.removeItem(key)
    else storage?.setItem(key, value)
  } catch {
    // In-memory state keeps the current tab usable when storage is unavailable.
  }
}

const sessionStore = (): Storage | undefined =>
  typeof window === 'undefined' ? undefined : window.sessionStorage

const persistentStore = (): Storage | undefined =>
  typeof window === 'undefined' ? undefined : window.localStorage

const token = (): string | null => {
  if (memoryToken) return memoryToken
  const current = readStorage(
    sessionStore(),
    ADS_MANAGER_STORAGE_KEYS.TOKEN,
  )
  if (current) {
    memoryToken = current
    return current
  }
  const legacy = readStorage(
    persistentStore(),
    ADS_MANAGER_STORAGE_KEYS.TOKEN,
  )
  if (!legacy) return null
  memoryToken = legacy
  writeStorage(sessionStore(), ADS_MANAGER_STORAGE_KEYS.TOKEN, legacy)
  writeStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.TOKEN)
  return legacy
}

const advertiserId = (): string | null =>
  readStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.ADVERTISER_ID)

const protectedHeaders = (): Record<string, string> => ({
  ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  ...(advertiserId()
    ? { 'x-ads-advertiser-id': advertiserId() as string }
    : {}),
})

const publicOptions = { requiresAuth: false as const }
const silentPublicOptions = { ...publicOptions, silent: true as const }
const protectedOptions = () => ({
  requiresAuth: false as const,
  headers: protectedHeaders(),
})
const silentProtectedOptions = () => ({
  ...protectedOptions(),
  silent: true as const,
})

interface CampaignDetailWire {
  campaign: AdsManagerCampaign
  creative?: CoreCreative
  asset_urls?: Record<string, string>
  asset_url?: string
  asset_type?: 'image' | 'video'
  link_asset_url?: string
  active_advertiser_id?: string
}

const normalizeCampaignDetail = (wire: CampaignDetailWire): CampaignDetail => {
  const firstAssetId = wire.creative?.asset_ids?.[0]
  const linkAssetId = wire.creative?.link?.asset_id
  return {
    campaign: wire.campaign,
    creative: wire.creative,
    asset_url:
      wire.asset_url ??
      wire.campaign.asset_url ??
      (firstAssetId ? wire.asset_urls?.[firstAssetId] : undefined),
    asset_type:
      wire.asset_type ?? wire.campaign.ads_manager?.creative_asset_type,
    link_asset_url:
      wire.link_asset_url ??
      (linkAssetId ? wire.asset_urls?.[linkAssetId] : undefined),
    active_advertiser_id:
      wire.active_advertiser_id ?? wire.campaign.advertiser_id,
  }
}

const getCampaignDetail = async (id: string): Promise<CampaignDetail> =>
  normalizeCampaignDetail(
    await apiClient.get<CampaignDetailWire>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}`,
      protectedOptions(),
    ),
  )

export const saveAdsManagerSession = (session: AdsManagerSession): void => {
  memoryToken = session.token
  writeStorage(sessionStore(), ADS_MANAGER_STORAGE_KEYS.TOKEN, session.token)
  writeStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.TOKEN)
  if (session.active_advertiser_id) {
    writeStorage(
      persistentStore(),
      ADS_MANAGER_STORAGE_KEYS.ADVERTISER_ID,
      session.active_advertiser_id,
    )
  } else {
    writeStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.ADVERTISER_ID)
  }
  notifyAdsManagerSessionChanged()
}

export const saveActiveAdvertiser = (id: string): void => {
  writeStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.ADVERTISER_ID, id)
  notifyAdsManagerSessionChanged()
}

export const getActiveAdvertiserId = (): string | undefined =>
  advertiserId() ?? undefined

export const clearAdsManagerSession = (): void => {
  memoryToken = undefined
  writeStorage(sessionStore(), ADS_MANAGER_STORAGE_KEYS.TOKEN)
  writeStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.TOKEN)
  writeStorage(persistentStore(), ADS_MANAGER_STORAGE_KEYS.ADVERTISER_ID)
  notifyAdsManagerSessionChanged()
}

export const hasAdsManagerSession = (): boolean => Boolean(token())

/** Returns the current Ads Manager bearer value for same-origin admin proxies. */
export const getAdsManagerAuthorizationHeader = (): string | undefined => {
  const currentToken = token()
  return currentToken ? `Bearer ${currentToken}` : undefined
}

/** Resolves the active role while remaining compatible with older API sessions. */
export const resolveAdsManagerActiveAccess = (
  session: Omit<AdsManagerSession, 'token'> | AdsManagerSession,
): AdsManagerActiveAdvertiserAccess => {
  if (session.active_advertiser_access) {
    return session.active_advertiser_access
  }
  const membership = session.memberships.find(
    (item) => item.advertiser_id === session.active_advertiser_id,
  )
  if (session.user.is_admin) {
    return {
      role: membership?.role,
      access_level: membership?.access_level ?? 'managed',
      is_impersonating: Boolean(session.active_advertiser_id && !membership),
      campaign: 'write',
      advertiser_profile: 'write',
      billing: 'read',
    }
  }
  if (!membership) {
    return {
      is_impersonating: false,
      campaign: session.active_advertiser_id ? 'read' : 'write',
      advertiser_profile: 'read',
      billing: 'none',
    }
  }
  const writable = membership.role !== 'viewer'
  return {
    role: membership.role,
    access_level: membership.access_level,
    is_impersonating: false,
    campaign: writable ? 'write' : 'read',
    advertiser_profile: writable ? 'write' : 'read',
    billing:
      writable && membership.access_level !== 'managed' ? 'write' : 'none',
  }
}

export const adsManagerApi = {
  getConfig: () =>
    apiClient.get<AdsManagerPublicConfig>('/v1/ads/config', publicOptions),

  targetOptions: (scope: TargetScope, query = '', limit = 40) =>
    apiClient.get<{ options: TargetOption[]; source: ReachEstimate['source'] }>(
      `/v1/ads/targeting/options?scope=${encodeURIComponent(scope)}&q=${encodeURIComponent(query)}&limit=${encodeURIComponent(limit)}`,
      publicOptions,
    ),

  estimateTarget: (target: TargetSelection) =>
    apiClient.post<ReachEstimate>(
      '/v1/ads/targeting/estimate',
      target,
      publicOptions,
    ),

  unfurl: (url: string) =>
    apiClient.post<UnfurlResult>('/v1/ads/unfurl', { url }, publicOptions),

  register: (email: string, password: string) =>
    apiClient.post<{
      user_id: string
      verification_required: true
      dev_verification_code?: string
    }>('/v1/ads/auth/register', { email, password }, silentPublicOptions),

  verifyEmail: (userId: string, code: string) =>
    apiClient.post<AdsManagerSession>(
      '/v1/ads/auth/verify-email',
      { user_id: userId, code },
      silentPublicOptions,
    ),

  passwordLogin: (email: string, password: string) =>
    apiClient.post<
      | AdsManagerSession
      | {
          verification_required: true
          user_id: string
          dev_verification_code?: string
        }
    >('/v1/ads/auth/login', { email, password }, silentPublicOptions),

  googleLogin: (credential: string) =>
    apiClient.post<AdsManagerSession>(
      '/v1/ads/auth/google',
      { credential },
      silentPublicOptions,
    ),

  forgotPassword: (email: string) =>
    apiClient.post<{ accepted: true; dev_reset_code?: string }>(
      '/v1/ads/auth/password/forgot',
      { email },
      silentPublicOptions,
    ),

  resetPassword: (email: string, code: string, password: string) =>
    apiClient.post<AdsManagerSession>(
      '/v1/ads/auth/password/reset',
      { email, code, password },
      silentPublicOptions,
    ),

  me: () =>
    apiClient.get<Omit<AdsManagerSession, 'token'>>(
      '/v1/ads/me',
      silentProtectedOptions(),
    ),

  listAdvertisers: (cursor?: string) =>
    apiClient.get<{
      advertisers: AdsAdvertiserSummary[]
      cursor?: string
    }>(
      `/v1/ads/advertisers?limit=500${
        cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
      }`,
      protectedOptions(),
    ),

  getAdvertiser: (id: string) =>
    apiClient.get<AdsAdvertiserSummary>(
      `/v1/ads/advertisers/${encodeURIComponent(id)}`,
      protectedOptions(),
    ),

  updateAdvertiser: (id: string, name: string) =>
    apiClient.patch<AdsAdvertiserSummary>(
      `/v1/ads/advertisers/${encodeURIComponent(id)}`,
      { name },
      protectedOptions(),
    ),

  listBrands: () =>
    apiClient.get<{ brands: AdsBrandIdentity[] }>(
      '/v1/ads/brands',
      protectedOptions(),
    ),

  createBrand: (input: {
    label: string
    name: string
    icon_asset_id: string
  }) =>
    apiClient.post<AdsBrandIdentity>(
      '/v1/ads/brands',
      input,
      protectedOptions(),
    ),

  createUpload: (input: {
    kind: 'creative' | 'brand_icon'
    content_type: 'png' | 'jpeg' | 'webp' | 'gif' | 'mp4' | 'mov'
    width: number
    height: number
    content_length: number
  }) =>
    apiClient.post<{
      asset_id: string
      asset_type: 'image' | 'video'
      upload_url: string
      upload_headers: Record<string, string>
    }>('/v1/ads/uploads', input, protectedOptions()),

  createCampaign: async (input: AdsManagerCampaignInput) => {
    const campaign = await apiClient.post<AdsManagerCampaign>(
      '/v1/ads/campaigns',
      { ...input, workflow: 'self_serve' },
      protectedOptions(),
    )
    return getCampaignDetail(campaign.campaign_id)
  },

  listCampaigns: () =>
    apiClient
      .get<{ campaigns: AdsManagerCampaign[] }>(
        '/v1/ads/campaigns?view=summary',
        protectedOptions(),
      )
      .then(({ campaigns }) => ({
        campaigns: campaigns.map((campaign) => ({ campaign })),
      })),

  campaignOverview: () =>
    apiClient
      .get<{
        campaigns: AdsManagerCampaign[]
        metrics: Record<string, DashboardMetrics>
        failed_campaign_ids: string[]
      }>('/v1/ads/campaigns?view=overview', silentProtectedOptions())
      .then(({ campaigns, metrics, failed_campaign_ids }) => ({
        campaigns: campaigns.map((campaign) => ({ campaign })),
        metrics,
        failed_campaign_ids,
      })),

  getCampaign: getCampaignDetail,

  updateCampaign: async (
    id: string,
    input: Partial<AdsManagerCampaignInput>,
  ) => {
    await apiClient.patch<AdsManagerCampaign>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}`,
      { ...input, workflow: 'self_serve' },
      protectedOptions(),
    )
    return getCampaignDetail(id)
  },

  submitCampaign: (id: string, paymentIntentId?: string) =>
    apiClient.post<CampaignDetail>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}/submit`,
      { payment_intent_id: paymentIntentId },
      protectedOptions(),
    ),

  retryReview: (id: string) =>
    apiClient.post<CampaignDetail>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}/retry-review`,
      {},
      protectedOptions(),
    ),

  changeStatus: async (
    id: string,
    status: 'pause' | 'resume' | 'stop',
  ) => {
    await apiClient.post<AdsManagerCampaign>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}/${status}`,
      {},
      protectedOptions(),
    )
    return getCampaignDetail(id)
  },

  metrics: (id: string) =>
    apiClient.get<DashboardMetrics>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}/dashboard`,
      protectedOptions(),
    ),

  campaignAnalytics: (id: string, range: CampaignAnalyticsRange) =>
    apiClient.get<CampaignAnalytics>(
      `/v1/ads/campaigns/${encodeURIComponent(id)}/analytics?range=${range}`,
      protectedOptions(),
    ),

  billing: (options?: { silent?: boolean }) =>
    apiClient.get<BillingSummary>('/v1/ads/billing', {
      ...protectedOptions(),
      silent: options?.silent,
    }),

  updateBillingPreferences: (autoRechargeEnabled: boolean) =>
    apiClient.patch<BillingPreferenceResponse>(
      '/v1/ads/billing/preferences',
      { auto_recharge_enabled: autoRechargeEnabled },
      silentProtectedOptions(),
    ),

  billingPayments: (cursor?: string) =>
    apiClient.get<CursorPage<BillingPayment>>(
      `/v1/ads/billing/payments${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      silentProtectedOptions(),
    ),

  billingActivity: (cursor?: string) =>
    apiClient.get<CursorPage<BillingActivity>>(
      `/v1/ads/billing/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      silentProtectedOptions(),
    ),

  createPaymentIntent: (amountCents: number, saveForAutoRecharge: boolean) =>
    apiClient.post<
      | {
          mode: 'stripe'
          payment_intent_id: string
          client_secret: string
          status: string
        }
      | {
          mode: 'mock'
          payment_intent_id: string
          status: 'succeeded'
        }
    >(
      '/v1/ads/billing/payment-intents',
      {
        amount_cents: amountCents,
        enable_auto_recharge: saveForAutoRecharge,
      },
      silentProtectedOptions(),
    ),

  reconcilePayment: (paymentIntentId: string) =>
    apiClient.post<BillingPaymentReconcileResponse>(
      `/v1/ads/billing/payment-intents/${encodeURIComponent(paymentIntentId)}/reconcile`,
      {},
      silentProtectedOptions(),
    ),

  getAdminSettings: () =>
    apiClient.get<AdsSettings>(
      '/v1/ads/admin/settings',
      protectedOptions(),
    ),

  grantAdminCredits: (
    advertiserId: string,
    amountCents: number,
    reason: string,
    idempotencyKey: string,
  ) =>
    apiClient.post<AdminCreditGrantResult>(
      `/v1/ads/admin/advertisers/${encodeURIComponent(advertiserId)}/credit-grants`,
      {
        amount_cents: amountCents,
        reason,
        idempotency_key: idempotencyKey,
      },
      protectedOptions(),
    ),

  grantAdminCreditsByEmail: (
    email: string,
    amountCents: number,
    reason: string,
    idempotencyKey: string,
  ) =>
    apiClient.post<AdminEmailCreditGrantResult>(
      '/v1/ads/admin/email-credit-grants',
      {
        email,
        amount_cents: amountCents,
        reason,
        idempotency_key: idempotencyKey,
      },
      protectedOptions(),
    ),

  getAdminAthenaCosts: () =>
    apiClient.get<AdsAthenaCostReport>(
      '/v1/ads/admin/athena-costs',
      protectedOptions(),
    ),

  updateAdminSettings: (input: Partial<AdsSettings>) =>
    apiClient.patch<AdsSettings>(
      '/v1/ads/admin/settings',
      input,
      protectedOptions(),
    ),

  overrideCampaignCpm: (campaignId: string, cpmCents: number) =>
    apiClient.patch<AdminCampaignPricingResult>(
      `/v1/ads/admin/campaigns/${encodeURIComponent(campaignId)}/pricing`,
      { cpm_cents: cpmCents },
      protectedOptions(),
    ),

  getReachCatalogSource: (snapshotMonth: string) =>
    apiClient.get<{ rows: ReachCatalogSourceRow[] }>(
      `/v1/ads/admin/reach/source?snapshot_month=${encodeURIComponent(snapshotMonth)}`,
      protectedOptions(),
    ),

  syncReach: (input: ReachCatalogSyncInput) =>
    apiClient.post<{ synced: number }>(
      '/v1/ads/admin/reach/sync',
      input,
      protectedOptions(),
    ),
}
